/**
 * MT Furnitures Header Component
 * Handles mobile menu interactions and navigation
 */

class MTFurnituresHeader {
  constructor() {
    this.header = document.querySelector('.mt-furnitures-header');
    this.menuToggle = document.querySelector('.mobile-menu-toggle');
    this.menuClose = document.querySelector('.mobile-menu-close');
    this.menuDrawer = document.querySelector('.mobile-menu-drawer');
    this.menuOverlay = document.querySelector('.mobile-menu-overlay');
    this.navToggles = document.querySelectorAll('.mobile-nav-toggle');
    this.body = document.body;
    
    // Search modal elements
    this.searchModal = document.querySelector('.search-modal');
    this.searchModalOverlay = document.querySelector('.search-modal-overlay');
    this.searchModalClose = document.querySelector('.search-modal-close');
    this.searchIcons = document.querySelectorAll('.search-icon');
    this.searchInput = document.querySelector('.search-modal-input');
    
    // Cart drawer elements
    this.cartTriggers = document.querySelectorAll('[data-cart-drawer-trigger]');
    
    // Scroll detection variables
    this.lastScrollY = 0;
    this.scrollThreshold = 10; // 滚动多少像素后触发
    this.ticking = false;

    this.init();
  }

  init() {
    if (!this.menuDrawer) return;

    // Add body class for sticky header spacing
    this.body.classList.add('has-sticky-header');

    // Bind event listeners
    this.bindEvents();
    this.initScrollBehavior();
    this.initSearchModal();
    this.initCartDrawer();
    // Init desktop hover behaviour to avoid accidental close when moving cursor
    this.initDesktopHover();
  }

  // Desktop hover handling: add small delay on hide so moving from nav-link to mega-menu
  // doesn't close the menu prematurely.
  initDesktopHover() {
    // Only apply on devices that support pointer hover
    if (window.matchMedia && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.desktopHoverDelay = 200; // ms
    this._desktopHideTimer = null;
    this._currentOpenNav = null;

    const navItems = document.querySelectorAll('.header-desktop .nav-item');
    navItems.forEach(item => {
      const mega = item.querySelector('.mega-menu');

      item.addEventListener('mouseenter', () => {
        // cancel any pending hide
        if (this._desktopHideTimer) {
          clearTimeout(this._desktopHideTimer);
          this._desktopHideTimer = null;
        }
        this.openDesktopMenu(item);
      });

      item.addEventListener('mouseleave', () => {
        // schedule hide with small delay
        this._desktopHideTimer = setTimeout(() => {
          this.closeDesktopMenu(item);
        }, this.desktopHoverDelay);
      });

      if (mega) {
        // keep menu open while cursor is over the mega menu
        mega.addEventListener('mouseenter', () => {
          if (this._desktopHideTimer) {
            clearTimeout(this._desktopHideTimer);
            this._desktopHideTimer = null;
          }
        });

        mega.addEventListener('mouseleave', () => {
          this._desktopHideTimer = setTimeout(() => {
            this.closeDesktopMenu(item);
          }, this.desktopHoverDelay);
        });
      }
    });
  }

  openDesktopMenu(item) {
    if (this._currentOpenNav && this._currentOpenNav !== item) {
      this.closeDesktopMenu(this._currentOpenNav);
    }
    item.classList.add('hover-open');
    this._currentOpenNav = item;
  }

  closeDesktopMenu(item) {
    if (!item) return;
    item.classList.remove('hover-open');
    if (this._currentOpenNav === item) this._currentOpenNav = null;
  }

  bindEvents() {
    // Open mobile menu
    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => this.openMenu());
    }

    // Close mobile menu
    if (this.menuClose) {
      this.menuClose.addEventListener('click', () => this.closeMenu());
    }

    // Close menu when clicking overlay
    if (this.menuOverlay) {
      this.menuOverlay.addEventListener('click', () => this.closeMenu());
    }

    // Handle navigation toggles (sliding panels)
    this.navToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.openPanel(toggle);
      });
    });

    // Handle back buttons
    const backButtons = document.querySelectorAll('.mobile-nav-back');
    backButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closePanel(button);
      });
    });

    // Close menu on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menuDrawer.classList.contains('active')) {
        this.closeMenu();
      }
    });

    // Handle window resize
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 990 && this.menuDrawer.classList.contains('active')) {
        this.closeMenu();
      }
    });
  }

  initSearchModal() {
    if (!this.searchModal) return;

    // Open search modal when clicking search icons
    this.searchIcons.forEach(icon => {
      icon.addEventListener('click', (e) => {
        e.preventDefault();
        this.openSearchModal();
      });
    });

    // Close search modal when clicking close button
    if (this.searchModalClose) {
      this.searchModalClose.addEventListener('click', () => {
        this.closeSearchModal();
      });
    }

    // Close search modal when clicking overlay
    if (this.searchModalOverlay) {
      this.searchModalOverlay.addEventListener('click', () => {
        this.closeSearchModal();
      });
    }

    // Close search modal on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.searchModal.classList.contains('active')) {
        this.closeSearchModal();
      }
    });
  }

  openSearchModal() {
    this.searchModal.classList.add('active');
    this.body.classList.add('search-modal-open');
    
    // Focus on search input after animation
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 400);
  }

  closeSearchModal() {
    this.searchModal.classList.remove('active');
    this.body.classList.remove('search-modal-open');
    
    // Clear search input
    if (this.searchInput) {
      this.searchInput.value = '';
    }
  }

  initCartDrawer() {
    // Open cart drawer when clicking cart triggers
    this.cartTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openCartDrawer();
      });
    });
  }

  openCartDrawer() {
    // 使用全局函数打开购物车抽屉（与 quickview 使用相同的弹窗）
    if (typeof window.openCartDrawer === 'function') {
      window.openCartDrawer();
    } else {
      console.warn('window.openCartDrawer function not found. Make sure mt-cart-drawer.js is loaded.');
    }
  }

  initScrollBehavior() {
    if (!this.header) return;

    // 只根据滚动位置调整背景 (scrolled)，不根据滚动方向隐藏 header，保证始终可见。
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // 添加/移除 scrolled 类（改变背景色）
      if (currentScrollY > 50) {
        this.header.classList.add('scrolled');
      } else {
        this.header.classList.remove('scrolled');
      }

      // 重置 ticking 标志
      this.ticking = false;
    };

    // 使用 requestAnimationFrame 优化滚动性能
    window.addEventListener('scroll', () => {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
        });
        this.ticking = true;
      }
    }, { passive: true });
  }

  openMenu() {
    this.menuDrawer.classList.add('active');
    this.body.classList.add('mobile-menu-open');
    
    // Trap focus within menu
    this.trapFocus();
  }

  closeMenu() {
    this.menuDrawer.classList.remove('active');
    this.body.classList.remove('mobile-menu-open');
    
    // Reset all panels to initial state after animation
    setTimeout(() => {
      const allPanels = document.querySelectorAll('.mobile-nav-panel');
      allPanels.forEach(panel => {
        panel.classList.remove('active', 'sliding-out');
      });
      // Activate level 1
      const level1 = document.querySelector('.mobile-nav-level-1');
      if (level1) {
        level1.classList.add('active');
      }
    }, 300);
  }

  openPanel(toggle) {
    const level = toggle.getAttribute('data-level');
    const parent = toggle.getAttribute('data-parent');
    
    // Find the target panel
    const targetPanel = document.querySelector(`.mobile-nav-level-${level}[data-parent="${parent}"]`);
    if (!targetPanel) return;

    // Find current active panel
    const currentPanel = document.querySelector('.mobile-nav-panel.active');
    
    // Slide out current panel
    if (currentPanel) {
      currentPanel.classList.add('sliding-out');
    }
    
    // Slide in new panel
    targetPanel.classList.add('active');
  }

  closePanel(backButton) {
    const currentPanel = backButton.closest('.mobile-nav-panel');
    if (!currentPanel) return;

    const currentLevel = currentPanel.classList.contains('mobile-nav-level-2') ? 2 : 3;
    const previousLevel = currentLevel - 1;
    
    // Find previous panel
    let previousPanel;
    if (currentLevel === 2) {
      previousPanel = document.querySelector('.mobile-nav-level-1');
    } else if (currentLevel === 3) {
      // Find the parent level 2 panel
      const currentParent = currentPanel.getAttribute('data-parent');
      // We need to find which level 2 panel contains this item
      const allLevel2Panels = document.querySelectorAll('.mobile-nav-level-2');
      allLevel2Panels.forEach(panel => {
        const items = panel.querySelectorAll('.mobile-nav-link');
        items.forEach(item => {
          if (item.textContent.trim() === currentParent) {
            previousPanel = panel;
          }
        });
      });
    }

    if (previousPanel) {
      // Remove sliding-out from previous panel
      previousPanel.classList.remove('sliding-out');
      previousPanel.classList.add('active');
      
      // Remove current panel
      currentPanel.classList.remove('active');
    }
  }

  toggleSubmenu(toggle) {
    // This method is no longer used but kept for compatibility
    const navItem = toggle.closest('.mobile-nav-item');
    const isActive = navItem.classList.contains('active');

    // Close all other submenus
    document.querySelectorAll('.mobile-nav-item.active').forEach(item => {
      if (item !== navItem) {
        item.classList.remove('active');
      }
    });

    // Toggle current submenu
    navItem.classList.toggle('active');
  }

  trapFocus() {
    const focusableElements = this.menuDrawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled])'
    );
    
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    this.menuDrawer.addEventListener('keydown', handleTabKey);
  }
}

// Initialize header when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MTFurnituresHeader();
  });
} else {
  new MTFurnituresHeader();
}

// Handle cart count updates (robust when event dispatch omits detail)
document.addEventListener('cart:updated', async (event) => {
  try {
    let itemCount = 0;

    // If event.detail is provided and contains item_count, use it
    if (event && event.detail && typeof event.detail.item_count !== 'undefined') {
      itemCount = event.detail.item_count;
    } else if (event && event.detail && typeof event.detail === 'object' && event.detail !== null && typeof event.detail.items !== 'undefined') {
      // In case full cart object was passed under detail
      itemCount = event.detail.item_count || (Array.isArray(event.detail.items) ? event.detail.items.length : 0);
    } else {
      // Fallback: fetch the cart from the server
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (res && res.ok) {
          const cart = await res.json();
          itemCount = cart.item_count || 0;
        }
      } catch (fetchErr) {
        console.error('Failed to fetch cart for count fallback:', fetchErr);
      }
    }

    const cartCounts = document.querySelectorAll('.cart-count, .cart-count-badge');
    cartCounts.forEach(count => {
      count.textContent = itemCount || 0;
    });
  } catch (err) {
    console.error('Error updating cart count:', err);
  }
});
